import {getLanguageCode} from './localization';
import {interpolate} from './stringFormat';

/** Mirrors the AEM plural-forms fragment: CLDR categories, each a template holding a `{0}` count placeholder. */
export interface PluralForms {
    zero?: string | null;
    one?: string | null;
    two?: string | null;
    few?: string | null;
    many?: string | null;
    other?: string | null;
}

/**
 * Picks the plural template for `count` using CLDR rules (en/ru/uk/pl/ar/ja/zh/ko/...).
 *
 * `Intl.PluralRules` never reports `zero` for languages like English, so an authored
 * `zero` form wins for count 0. Missing categories fall back to `other`.
 */
export const selectPluralForm = (
    count: number,
    forms: PluralForms | undefined | null,
    locale: string = getLanguageCode()
): string | undefined => {
    if (!forms) return undefined;

    if (count === 0 && forms.zero != null) return forms.zero;

    const rule = new Intl.PluralRules(locale).select(count);

    return forms[rule as keyof PluralForms] ?? forms.other ?? undefined;
};

/**
 * Formats a count against locale-aware plural forms.
 *
 * formatPlural(3, {one: '{0} action left', other: '{0} actions left'}, 'en') -> '3 actions left'
 */
export const formatPlural = (
    count: number,
    forms: PluralForms | undefined | null,
    locale: string = getLanguageCode()
): string | undefined => {
    const template = selectPluralForm(count, forms, locale);

    return template === undefined ? undefined : interpolate(template, count);
};
